import { styled } from '@mui/material';
import * as React from 'react';
import type { JSX } from 'react';

import { colorSystem, disabledStyles } from '../../theme';
import { CircularProgress } from '../CircularProgress';
import { Switch } from '../Switch';
import { Text } from '../Text';

const PADDING = 4;

export function SwitchableChip({
    checked,
    label,
    isLoading,
    isDisabled,
    ContainerProps,
}: {
    checked: boolean;
    label: string;
    isLoading?: boolean;
    isDisabled?: boolean;
    ContainerProps?: React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>;
}): JSX.Element {
    return (
        <StyledSpan {...ContainerProps} isDisabled={isDisabled}>
            <GreenSwitch checked={checked} disabled={isLoading || isDisabled} />
            <Text
                variant="h5"
                color="inherit"
                style={{
                    marginRight: isLoading ? PADDING : 0,
                }}
            >
                {label}
            </Text>
            {isLoading && <CircularProgress size="small" />}
        </StyledSpan>
    );
}

const GreenSwitch = styled(Switch)({
    padding: 0,
    '& .MuiSwitch-switchBase': {
        padding: 0,
        margin: 2,
        transitionDuration: '300ms',
        '&:hover': {
            '& + .MuiSwitch-track': {
                backgroundColor: colorSystem.neutral[7],
            },
        },
        '&.Mui-checked': {
            color: colorSystem.neutral.white,
            '& + .MuiSwitch-track': {
                backgroundColor: colorSystem.green[5],
                opacity: 1,
                border: 0,
            },
            '&.Mui-disabled + .MuiSwitch-track': {
                backgroundColor: colorSystem.green[4],
            },
        },
        '&.Mui-checked:hover': {
            '& + .MuiSwitch-track': {
                backgroundColor: colorSystem.green[7],
            },
        },
        '&.Mui-disabled .MuiSwitch-thumb': {
            color: colorSystem.neutral.white,
        },
        '&.Mui-disabled + .MuiSwitch-track': {
            opacity: 1,
            backgroundColor: colorSystem.neutral[3],
        },
    },
});

const StyledSpan = styled('span', {
    shouldForwardProp: (propName) => {
        return propName !== 'isDisabled';
    },
})<{ isDisabled?: boolean }>(({ isDisabled }) => ({
    border: `1px solid ${colorSystem.neutral[2]}`,
    borderRadius: 20,
    padding: '6px 10px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    transition: 'border-color 0.2s ease-in',
    ...(isDisabled ? { ...disabledStyles, color: colorSystem.neutral[6] } : {}),
    '&:hover': isDisabled
        ? { cursor: 'not-allowed' }
        : {
              cursor: 'pointer',
              border: `1px solid ${colorSystem.neutral[8]} !important`,
          },
}));
